/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { Button, TextField, Typography2, breakpoints, useToast } from '@coursera/cds-core';

import DataReportModal from 'bundles/account-settings/components/sections/data-report/DataReportModal';
import DataReleaseMutation from 'bundles/account-settings/components/sections/data-report/api/DataReleaseMutation.graphql';
import DataReportQuery from 'bundles/account-settings/components/sections/data-report/api/DataReportQuery.graphql';
import type {
  DataReleaseMutation as DataReleaseMutationData,
  DataReleaseMutationVariables,
} from 'bundles/account-settings/components/sections/data-report/api/__generated__/DataReleaseMutation';
import type {
  DataReportQuery as DataReportQueryData,
  DataReportQueryVariables,
} from 'bundles/account-settings/components/sections/data-report/api/__generated__/DataReportQuery';
import { useEnterpriseUserFeatures } from 'bundles/account-settings/contexts/EnterpriseUserFeaturesContext';

import _t from 'i18n!nls/account-settings';

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  headingGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  heading: css`
    && {
      margin: 0;
    }
  `,
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
};

const DataReport = () => {
  const { id: userId } = getUser();
  const { isUserSsoLoginForced, isUnifiedSso } = useEnterpriseUserFeatures();
  const { show: showToast } = useToast();
  const { data } = useQuery<DataReportQueryData, DataReportQueryVariables>(DataReportQuery, {
    skip: !isAuthenticatedUser(),
    variables: {
      userId,
      includeInvitations: false,
      first: 100,
    },
    errorPolicy: 'all',
    context: {
      clientName: 'gatewayGql',
    },
  });

  const isEnterpriseLearner =
    !!data?.ProgramMemberships?.queryProgramMembershipsByUserIdAndIncludeInvitations?.totalCount;

  const [showConfirmEmailModal, setShowConfirmEmailModal] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [showRateLimitNotification, setShowRateLimitNotification] = useState(false);

  const handleOpenModal = () => {
    setShowConfirmEmailModal(true);
  };

  const handleCloseModal = () => {
    setShowConfirmEmailModal(false);
  };

  const [releaseData] = useMutation<DataReleaseMutationData, DataReleaseMutationVariables>(DataReleaseMutation, {
    onCompleted: () => {
      handleCloseModal();
      setShowErrorNotification(false);
      setShowRateLimitNotification(false);
      setDisabled(false);
      showToast({
        message: _t('We are processing your request. You should receive an email soon.'),
        role: 'status',
        durationMs: 4000,
      });
    },
    onError: (error) => {
      if (error.message.includes('throttled')) {
        setShowErrorNotification(false);
        setShowRateLimitNotification(true);
        setDisabled(true);
      } else {
        setShowErrorNotification(true);
        setShowRateLimitNotification(false);
        setDisabled(true);
      }
    },
    variables: { userId },
    context: { clientName: 'gatewayGql' },
  });

  const handleDataRequest = () => {
    setDisabled(true);

    releaseData();
  };

  const dismissNotification = () => {
    setShowErrorNotification(false);
    setShowRateLimitNotification(false);
    setDisabled(false);
  };

  let notificationMessage;

  if (showErrorNotification) {
    notificationMessage = _t('The request was not successful. Please try again.');
  } else if (showRateLimitNotification) {
    notificationMessage = _t(
      'It looks like you already made a request today. Please wait at least 24 hours between requests for your learner data report.'
    );
  }

  return (
    <div css={styles.root}>
      {showConfirmEmailModal && (
        <DataReportModal
          handleCloseModal={handleCloseModal}
          handleDataRequest={handleDataRequest}
          isEnterpriseLearner={isEnterpriseLearner}
          disabled={disabled}
          showModal
          notificationMessage={notificationMessage}
          notificationType={showErrorNotification || showRateLimitNotification ? 'error' : undefined}
          onDismissNotification={dismissNotification}
        />
      )}
      <div css={styles.headingGroup}>
        <Typography2 component="h2" variant="subtitleLarge" css={styles.heading}>
          {_t('Learner data report')}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {_t(
            'Request a report of all learner data stored by Coursera about your account. This report will be sent to the e-mail address below.'
          )}
        </Typography2>
      </div>
      <TextField value={getUser().email_address} readOnly fullWidth aria-label={_t('Email address')} />
      <div>
        <Button variant="secondary" size="medium" onClick={handleOpenModal} css={styles.button}>
          {_t('Send report')}
        </Button>
      </div>
      {!(isUserSsoLoginForced || isUnifiedSso) && (
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {_t('To change email, please adjust the email settings at the top of this page.')}
        </Typography2>
      )}
    </div>
  );
};

export default DataReport;
